'use client';

import {
    createContext,
    useContext,
    useEffect,
    useLayoutEffect,
    useReducer,
    useRef,
    useSyncExternalStore,
    type Dispatch,
    type ReactNode,
    type RefObject,
} from 'react';
import { Pause, Play, Volume2, VolumeX } from 'lucide-react';

import { usePestanaOculta, usePrefiereMovimientoReducido } from '@/components/asistente/movimientoReducido';
import { useEsEscritorio } from '@/components/marketing/comun/useEsEscritorio';
import type { VideoHero } from '@/content/videoHero';

import {
    ESTADO_INICIAL_VIDEO,
    animarRespaldo,
    estadoVisible,
    intentaReproducir,
    modoSubtitulos,
    mostrarControles,
    mostrarSonido,
    reducirVideoHeroe,
    videoVisible,
    type EntornoVideoHeroe,
    type EstadoVideoHeroe,
    type EstadoVisibleVideo,
    type EventoVideoHeroe,
    type ModoSubtitulos,
} from './estadoVideoHeroe';
import { CONTROLES_VIDEO_HEROE, ESCENARIO_HEROE } from './textosHeroe';
import { useEnPantalla } from './useEnPantalla';

// Sin «playing» en 4 s desde que se pide reproducir, el vídeo no se enseña y corre la animación (UI-SPEC §B.3).
const ESPERA_MAXIMA_MS = 4000;
// Por debajo de un 25 % visible el vídeo se pausa solo.
const UMBRAL_EN_PANTALLA = 0.25;

// ---------- entorno: se lee en cada render, nunca se copia a un estado ----------

/** Lo justo de la Network Information API: no está en todos los navegadores ni en lib.dom. */
interface ConexionConAhorro extends EventTarget {
    saveData?: boolean;
}

function conexion(): ConexionConAhorro | null {
    if (typeof navigator === 'undefined') return null;
    return (navigator as Navigator & { connection?: ConexionConAhorro }).connection ?? null;
}

function suscribirAhorro(aviso: () => void): () => void {
    const actual = conexion();
    actual?.addEventListener('change', aviso);
    return () => actual?.removeEventListener('change', aviso);
}

function leerAhorro(): boolean {
    return conexion()?.saveData === true;
}

function falsoEnServidor(): boolean {
    return false;
}

function sinSuscripcion(): () => void {
    return () => undefined;
}

function verdaderoEnCliente(): boolean {
    return true;
}

function useEntornoVideo(raizRef: RefObject<HTMLDivElement | null>): EntornoVideoHeroe {
    const hidratado = useSyncExternalStore(sinSuscripcion, verdaderoEnCliente, falsoEnServidor);
    const escritorio = useEsEscritorio();
    const reducido = usePrefiereMovimientoReducido();
    const ahorro = useSyncExternalStore(suscribirAhorro, leerAhorro, falsoEnServidor);
    const oculta = usePestanaOculta();
    const enPantalla = useEnPantalla(raizRef, escritorio, UMBRAL_EN_PANTALLA);
    return { hidratado, escritorio, bloqueado: reducido || ahorro, enPantalla, pestanaVisible: !oculta };
}

// ---------- efectos: solo mueven el elemento <video> ----------

function esAborto(error: unknown): boolean {
    return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
}

// `muted` va como propiedad antes de play() (useMudo corre antes): la reproducción automática solo se
// permite sin sonido. Un AbortError es nuestra propia pausa interrumpiendo el play(), no un fallo.
function useReproduccion(refVideo: RefObject<HTMLVideoElement | null>, intenta: boolean, despachar: Dispatch<EventoVideoHeroe>) {
    useEffect(() => {
        const video = refVideo.current;
        if (!video) return;
        if (!intenta) {
            if (!video.paused) video.pause();
            return;
        }
        Promise.resolve(video.play()).catch((error: unknown) => {
            if (!esAborto(error)) despachar({ tipo: 'fallo' });
        });
    }, [refVideo, intenta, despachar]);
}

function useTiempoMaximo(esperando: boolean, despachar: Dispatch<EventoVideoHeroe>) {
    useEffect(() => {
        if (!esperando) return;
        const id = window.setTimeout(() => despachar({ tipo: 'tiempoAgotado' }), ESPERA_MAXIMA_MS);
        return () => window.clearTimeout(id);
    }, [esperando, despachar]);
}

function useMudo(refVideo: RefObject<HTMLVideoElement | null>, conSonido: boolean) {
    useEffect(() => {
        if (refVideo.current) refVideo.current.muted = !conSonido;
    }, [refVideo, conSonido]);
}

// El primer «Activar sonido» vuelve al segundo 0 para que la narración empiece por su primera palabra.
function useReinicio(refVideo: RefObject<HTMLVideoElement | null>, pendiente: boolean, despachar: Dispatch<EventoVideoHeroe>) {
    useEffect(() => {
        if (!pendiente || !refVideo.current) return;
        refVideo.current.currentTime = 0;
        despachar({ tipo: 'reiniciado' });
    }, [refVideo, pendiente, despachar]);
}

// De layout, no pasivo: el modo cambia en el mismo commit que pasa el vídeo a opacity-100, así el primer
// fotograma visible ya lleva su cue (18-34). Con useEffect el cue llegaba un fotograma tarde.
function useSubtitulos(refVideo: RefObject<HTMLVideoElement | null>, modo: ModoSubtitulos) {
    useLayoutEffect(() => {
        const pista = refVideo.current?.textTracks?.[0];
        if (pista) pista.mode = modo;
    }, [refVideo, modo]);
}

// ---------- el hook y el contexto que leen el vídeo y la capa estática ----------

interface ValorCapaVideo {
    video: VideoHero;
    refVideo: RefObject<HTMLVideoElement | null>;
    visible: boolean;
    despachar: Dispatch<EventoVideoHeroe>;
}

const ContextoCapaVideo = createContext<ValorCapaVideo | null>(null);

export interface CapaVideoHeroe {
    estado: EstadoVideoHeroe;
    entorno: EntornoVideoHeroe;
    despachar: Dispatch<EventoVideoHeroe>;
    animarRespaldo: boolean;
    estadoVisible: EstadoVisibleVideo;
    valor: ValorCapaVideo;
}

/** El estado del vídeo del héroe de escritorio (UI-SPEC §B.3) y los efectos que mueven su <video>. */
export function useCapaVideoHeroe(video: VideoHero, raizRef: RefObject<HTMLDivElement | null>): CapaVideoHeroe {
    const [estado, despachar] = useReducer(reducirVideoHeroe, ESTADO_INICIAL_VIDEO);
    const entorno = useEntornoVideo(raizRef);
    const refVideo = useRef<HTMLVideoElement>(null);
    const intenta = intentaReproducir(estado, entorno);
    useMudo(refVideo, estado.conSonido);
    useReinicio(refVideo, estado.reinicioPendiente, despachar);
    useSubtitulos(refVideo, modoSubtitulos(estado, entorno));
    useReproduccion(refVideo, intenta, despachar);
    useTiempoMaximo(intenta && estado.fase === 'espera', despachar);
    const visible = videoVisible(estado, entorno);
    return {
        estado,
        entorno,
        despachar,
        animarRespaldo: animarRespaldo(estado, entorno),
        estadoVisible: estadoVisible(estado, entorno),
        valor: { video, refVideo, visible, despachar },
    };
}

export function ProveedorCapaVideo({ valor, children }: { valor: ValorCapaVideo; children: ReactNode }) {
    return <ContextoCapaVideo.Provider value={valor}>{children}</ContextoCapaVideo.Provider>;
}

/**
 * El vídeo, encima del fotograma estático y dentro de la caja interior de la tarjeta (UI-SPEC §B.2).
 * Sin `autoplay` (el navegador lo descargaría aunque el árbol esté oculto, y el móvil debe pedir cero
 * bytes) y sin `poster` (el póster es el fotograma estático, D-09): solo arranca por script.
 */
export function VideoHeroe() {
    const capa = useContext(ContextoCapaVideo);
    if (!capa) return null;
    const { video, refVideo, visible, despachar } = capa;
    const fallo = () => despachar({ tipo: 'fallo' });
    return (
        <video
            ref={refVideo}
            className={`video-marketing video-heroe absolute inset-0 h-full w-full rounded-[22px] object-cover ${visible ? 'opacity-100' : 'opacity-0'}`}
            width={video.ancho}
            height={video.alto}
            muted
            loop
            playsInline
            preload="none"
            disablePictureInPicture
            tabIndex={-1}
            aria-label={video.etiquetaAccesible}
            aria-hidden={visible ? undefined : true}
            onPlaying={() => despachar({ tipo: 'reproduciendo' })}
            onError={fallo}
        >
            {/* Un fallo de carga se dispara en <source>, no en <video> (18-07). */}
            <source src={video.ruta} type="video/mp4" onError={fallo} />
            <track kind="captions" srcLang="es" label="Español" src={video.subtitulos} />
        </video>
    );
}

/** El fotograma estático de la Fase 17: deja de leerse en voz alta cuando el vídeo lo tapa. */
export function CapaEstaticaHeroe({ children }: { children: ReactNode }) {
    const capa = useContext(ContextoCapaVideo);
    return (
        <div className="absolute inset-0" aria-hidden={capa?.visible ? true : undefined}>
            {children}
        </div>
    );
}

// ---------- el grupo de controles (UI-SPEC §B.4, AM-1) ----------

const CLASE_BOTON = 'flex h-11 items-center justify-center rounded-ds-md border-2 border-ink text-ink';

function fondoBoton(pulsado: boolean): string {
    return pulsado ? 'bg-tint-accent' : 'bg-surface-2 hover:bg-surface-1';
}

function BotonSonido({ conSonido, alPulsar }: { conSonido: boolean; alPulsar: () => void }) {
    const Icono = conSonido ? Volume2 : VolumeX;
    return (
        <button
            type="button"
            aria-pressed={conSonido}
            aria-label={conSonido ? CONTROLES_VIDEO_HEROE.ariaSilenciar : CONTROLES_VIDEO_HEROE.ariaActivarSonido}
            onClick={alPulsar}
            className={`${CLASE_BOTON} gap-2 px-4 text-[15px] font-bold ${fondoBoton(conSonido)}`}
        >
            <Icono size={20} aria-hidden="true" />
            {conSonido ? CONTROLES_VIDEO_HEROE.silenciar : CONTROLES_VIDEO_HEROE.activarSonido}
        </button>
    );
}

// WCAG 2.2.2: la pausa vale para el vídeo y, en el respaldo, para la animación (con su copy de la Fase 17).
// El nombre es fijo en los dos modos y el estado lo dice solo aria-pressed, como el botón móvil: un nombre
// que también cambiara se leería «Reanudar vídeo, pulsado», que se contradice (17-REVIEW-A IN-01).
function etiquetaPausa(conVideo: boolean): string {
    return conVideo ? CONTROLES_VIDEO_HEROE.pausarVideo : ESCENARIO_HEROE.pausar;
}

function BotonPausaVideo({ pausado, conVideo, alPulsar }: { pausado: boolean; conVideo: boolean; alPulsar: () => void }) {
    const Icono = pausado ? Play : Pause;
    return (
        <button
            type="button"
            aria-pressed={pausado}
            aria-label={etiquetaPausa(conVideo)}
            onClick={alPulsar}
            className={`${CLASE_BOTON} w-11 ${fondoBoton(pausado)}`}
        >
            <Icono size={20} aria-hidden="true" />
        </button>
    );
}

/**
 * Arriba a la derecha de la tarjeta, a 20 px de su caja interior y fuera del marco escalado, así sus
 * 44 px nunca encogen (AM-1). El marco mide 1316×652 de diseño y la caja interior empieza a 2 px
 * arriba y acaba a 6 px de la derecha (borde de 2 más sombra de 4), así que esos px escalan con el
 * marco: se expresan como porcentaje del alto y del ancho de la isla.
 */
export function ControlesVideoHeroe({ capa }: { capa: CapaVideoHeroe }) {
    const { estado, entorno, despachar } = capa;
    if (!mostrarControles(estado, entorno)) return null;
    return (
        <div data-controles-video="" className="absolute right-[calc(600%/1316_+_20px)] top-[calc(200%/652_+_20px)] z-10 flex gap-2">
            {mostrarSonido(estado, entorno) && <BotonSonido conSonido={estado.conSonido} alPulsar={() => despachar({ tipo: 'alternarSonido' })} />}
            <BotonPausaVideo
                pausado={estado.pausadoPorUsuario}
                conVideo={estado.fase === 'reproduciendo'}
                alPulsar={() => despachar({ tipo: 'alternarPausa' })}
            />
        </div>
    );
}
